import { IconLoader2, IconPlus, IconCircleXFilled } from '@tabler/icons-react'
import { Upload, UploadProps, App, Spin } from 'antd'
import Image from 'next/image'
import React, { MouseEventHandler, useCallback, useState } from 'react'

import { uploadImage } from '@/api/uploader'
import { getApiBaseUrl } from '@/utils/env'

import s from './image-uploader.module.css'

export interface ImageUploaderProps {
  creatorId: number
  disabled?: boolean
  value?: string
  onChange?: (value?: string) => void
}

export interface FileResponse {
  data: {
    name: string
    url: string
  }
}

export default function ImageUploader({
  creatorId,
  value,
  onChange,
  disabled,
}: ImageUploaderProps) {
  const [loading, setLoading] = useState(false)
  const { message } = App.useApp()

  const handleChange: NonNullable<UploadProps<FileResponse>['onChange']> =
    useCallback(
      ({ file: { status, response } }) => {
        if (status === 'uploading') {
          setLoading(true)
          return
        }
        if (status === 'done' && onChange) {
          onChange(response?.data.url)
          setLoading(false)
          return
        }
        if (status === 'error') {
          setLoading(false)
          void message.error('Upload failed.')
        }
      },
      [message, onChange],
    )

  const handleRemove: MouseEventHandler<SVGSVGElement> = useCallback(
    (event) => {
      event.stopPropagation()
      if (onChange) {
        onChange(undefined)
      }
    },
    [onChange],
  )

  const customRequest: NonNullable<UploadProps['customRequest']> = useCallback(
    async ({ onSuccess, onError, file }) => {
      try {
        const src = await uploadImage({
          creatorId,
          file: file as File,
        })
        if (onSuccess) {
          onSuccess({ data: { url: src } }, file)
        }
      } catch (e) {
        console.error(e)
        if (onError) {
          onError(new Error('Upload error'))
        }
      }
    },
    [creatorId],
  )

  return (
    <Upload<FileResponse>
      listType="picture-card"
      showUploadList={false}
      action={`${getApiBaseUrl()}file`}
      customRequest={customRequest}
      onChange={handleChange}
      accept="image/*"
      className={s.Upload}
      disabled={disabled}
    >
      {value ? (
        <Spin spinning={loading} wrapperClassName={s.UploadedWrapper}>
          <Image
            className={s.UploadedImage}
            src={value}
            alt="avatar"
            width={100}
            height={100}
          />
          <IconCircleXFilled className={s.Remove} onClick={handleRemove} />
        </Spin>
      ) : (
        <button type="button" disabled={disabled} className={s.UploadButton}>
          {loading ? <IconLoader2 className="anticon-spin" /> : <IconPlus />}
          <div className={s.UploadText}>
            {loading ? 'Uploading...' : 'Upload'}
          </div>
        </button>
      )}
    </Upload>
  )
}
